import { motion } from "framer-motion";
import { ArrowUpRight, CalendarCheck, Check, ClipboardList, Flame, HardHat, Thermometer } from "lucide-react";

const ancillary = [
  {
    id: "annual",
    icon: CalendarCheck,
    title: "Annual Maintenance Inspection",
    text: "Yearly whole-home health check that catches small issues before they become insurance claims.",
    price: "Starting at $325",
  },
  {
    id: "energy",
    icon: Flame,
    title: "Energy Loss Audit",
    text: "Thermal scan plus envelope review to find the leaks driving up your cooling bills.",
    price: "Starting at $275",
  },
  {
    id: "new-construction",
    icon: HardHat,
    title: "New Construction Inspection",
    text: "Pre-drywall and final-phase inspections that hold builders to Florida code before you close.",
    price: "Starting at $375",
  },
  {
    id: "pre-listing",
    icon: ClipboardList,
    title: "Pre-Listing Inspection",
    text: "Know what buyers' inspectors will find — fix it or price it in before you list.",
    price: "Starting at $350",
  },
  {
    id: "thermal",
    icon: Thermometer,
    title: "Standalone Thermal Imaging",
    text: "Infrared moisture, electrical hotspot, and insulation scan with a photo report.",
    price: "Starting at $195",
  },
];

const services = [
  {
    id: "pre-purchase",
    num: "01",
    tag: "BUYER PROTECTION",
    title: "Comprehensive Pre-Purchase Inspection",
    description:
      "300+ point full structural, mechanical, electrical, plumbing, and roof evaluation before closing — so you negotiate from evidence, not hope.",
    highlights: ["Foundation & Frame", "Roof & Attic Thermal Scan", "HVAC & Electrical Panels", "Plumbing & Appliance Safety"],
    price: "Starting at $395",
    img: "https://images.pexels.com/photos/12558962/pexels-photo-12558962.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    wide: true,
  },
  {
    id: "4-point",
    num: "02",
    tag: "INSURANCE MANDATORY",
    title: "Florida 4-Point Insurance Inspection",
    description:
      "Required by Florida insurers for homes 20+ years old — roof, electrical, HVAC and plumbing documented to carrier standards.",
    highlights: ["Roof System Age & Life", "Electrical Panel & Wiring", "HVAC Heating/Cooling", "Plumbing Supply & Drains"],
    price: "Starting at $175",
    img: "https://images.unsplash.com/photo-1583608205776-bfd35f0d9f83?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwxfHxmbG9yaWRhJTIwaG91c2UlMjBwYWxtJTIwdHJlZXMlMjBleHRlcmlvcnxlbnwwfHx8fDE3OTIwMDYwOTJ8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "wind-mitigation",
    num: "03",
    tag: "FL INSURANCE DISCOUNTS",
    title: "Wind Mitigation Discount Inspection",
    description:
      "Document hurricane-resistant features to unlock premium discounts — roof deck attachment, geometry, straps, and opening protection.",
    highlights: ["Roof Geometry (Hip vs Gable)", "Roof Deck Nail Spacing", "Roof-to-Wall Straps & Clips", "Impact Opening Protection"],
    price: "Starting at $150 — Save up to 40%",
    img: "https://images.unsplash.com/photo-1761418022097-621b807bea0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxmbG9yaWRhJTIwY29hc3RhbCUyMGhvbWUlMjBleHRlcmlvciUyMHBhbG0lMjB0cmVlcyUyMHN1bm55fGVufDB8fHx8MTc5Mjg3NzM2Mnww&ixlib=rb-4.1.0&q=85",
  },
];

const cardVariants = {
  hidden: { opacity: 0, y: 48 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.9, ease: [0.16, 1, 0.3, 1] } }),
};

export const Services = ({ onBook }) => (
  <section id="services" className="relative bg-sand py-28 sm:py-36 overflow-hidden">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
        <div>
          <div className="font-mono text-xs tracking-[0.25em] text-terra uppercase font-semibold mb-4">
            / Inspection Services
          </div>
          <h2 className="font-display font-bold tracking-tight text-obsidian text-2xl sm:text-3xl lg:text-4xl max-w-xl leading-tight">
            Three inspections. One defended investment.
          </h2>
        </div>
        <p className="text-slate-600 text-base max-w-sm leading-relaxed">
          Florida's climate and insurance market demand precision. Every report is
          same-day, photo-documented, and carrier-ready.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {services.map((s, i) => (
          <motion.article
            key={s.id}
            custom={i}
            variants={cardVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            data-testid={`service-card-${s.id}`}
            className={`group relative bg-paper rounded-2xl border border-sandline overflow-hidden transition-shadow duration-500 hover:shadow-2xl hover:shadow-obsidian/10 ${
              s.wide ? "lg:row-span-2 flex flex-col" : ""
            }`}
          >
            <div className={`relative overflow-hidden ${s.wide ? "h-72" : "h-48"}`}>
              <img
                src={s.img}
                alt={s.title}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-obsidian/25" />
              <div className="absolute top-4 left-4 flex items-center gap-2">
                <span className="font-mono text-[10px] tracking-[0.25em] bg-obsidian/85 backdrop-blur text-sand px-3 py-1.5 rounded-full">
                  {s.tag}
                </span>
              </div>
              <span className="absolute bottom-4 right-4 font-display font-black text-6xl text-sand/25 select-none">
                {s.num}
              </span>
            </div>
            <div className="p-7 flex-1 flex flex-col">
              <h3 className="font-display font-semibold text-xl sm:text-2xl tracking-tight text-obsidian">{s.title}</h3>
              <p className="mt-3 text-slate-600 text-sm sm:text-base leading-relaxed">{s.description}</p>
              <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                {s.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check size={14} className="text-flteal mt-0.5 shrink-0" /> {h}
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-6 flex items-center justify-between">
                <span className="font-mono text-xs tracking-wider text-terra font-semibold uppercase">{s.price}</span>
                <button
                  data-testid={`service-book-${s.id}`}
                  onClick={() => onBook({ service: s.title })}
                  className="flex items-center gap-1.5 text-sm font-semibold text-obsidian transition-colors hover:text-terra"
                >
                  Book this <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:rotate-45" />
                </button>
              </div>
            </div>
          </motion.article>
        ))}
      </div>

      <div className="mt-20">
        <div className="font-mono text-xs tracking-[0.25em] text-terra uppercase font-semibold mb-4">
          / Ancillary Services
        </div>
        <h3 className="font-display font-bold tracking-tight text-obsidian text-xl sm:text-2xl max-w-xl leading-tight mb-10">
          Beyond the big three — priced for South Florida.
        </h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="ancillary-services-grid">
          {ancillary.map((a, i) => (
            <motion.div
              key={a.id}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ delay: i * 0.08, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`ancillary-card-${a.id}`}
              className="group bg-paper border border-sandline rounded-2xl p-6 flex flex-col transition-shadow duration-500 hover:shadow-xl hover:shadow-obsidian/5"
            >
              <div className="w-11 h-11 rounded-xl bg-obsidian flex items-center justify-center mb-5 transition-colors duration-300 group-hover:bg-terra">
                <a.icon size={19} className="text-sun" />
              </div>
              <h4 className="font-display font-semibold text-lg tracking-tight text-obsidian">{a.title}</h4>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{a.text}</p>
              <div className="mt-5 pt-4 border-t border-sandline flex items-center justify-between">
                <span className="font-mono text-xs tracking-wider text-terra font-semibold uppercase">{a.price}</span>
                <button
                  data-testid={`ancillary-book-${a.id}`}
                  onClick={() => onBook({ service: a.title })}
                  className="flex items-center gap-1.5 text-sm font-semibold text-obsidian transition-colors hover:text-terra"
                >
                  Book this <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:rotate-45" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
